import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, MenuItem, Drawer, Stack, Button, Chip, Tooltip } from '@mui/material';
import { ChartBarIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import { useFilterParams } from '../../hooks/useFilterParams';
import {
  PageHeroBand,
  CardContainer,
  PrimaryButton,
  SecondaryButton,
  EmptyState,
  LoadingSkeleton,
  StatusChip,
} from '../../components/ui';
import { financeApi, RevenueChannel } from '../../api/financeApi';
import {
  aggregatorApi,
  AggregatorSummary,
  AggregatorEntry,
  AggregatorSettlement,
  AgingResponse,
  DuplicateAllocation,
  SuggestedAllocations,
} from '../../api/aggregatorApi';
import AggregatorCharts from '../../components/finance/AggregatorCharts';
import FinanceDateRange from '../../components/finance/FinanceDateRange';
import FilteredTotals from '../../components/finance/FilteredTotals';

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const DEFAULT_FILTERS = {
  channelId: '' as string,
  dateFrom: daysAgo(30),
  dateTo: daysAgo(0),
  entryStatus: '' as string, // '' = all, otherwise PENDING|PARTIAL|SETTLED
  charts: 'off',
};

const ENTRY_STATUS_CHIPS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'SETTLED', label: 'Settled' },
];

const AggregatorsPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Holds every revenue channel for the vendor. The chip strip and "aggregator
  // count" banner derive from the ONLINE_AGGREGATOR subset; the rest are still
  // loaded so we can show the empty-state CTA correctly when none are flagged.
  const [allChannels, setAllChannels] = useState<RevenueChannel[]>([]);
  const [filters, setFilters] = useFilterParams(DEFAULT_FILTERS);
  const channelId = filters.channelId ? Number(filters.channelId) : undefined;
  const dateFrom = filters.dateFrom;
  const dateTo = filters.dateTo;
  const setChannelId = useCallback(
    (id: number | undefined) => setFilters({ channelId: id != null ? String(id) : '' }),
    [setFilters],
  );
  const setDateFrom = useCallback((v: string) => setFilters({ dateFrom: v }), [setFilters]);
  const setDateTo = useCallback((v: string) => setFilters({ dateTo: v }), [setFilters]);
  const entryStatus = filters.entryStatus;
  const setEntryStatus = useCallback((v: string) => setFilters({ entryStatus: v }), [setFilters]);
  const chartsVisible = (filters.charts as string) !== 'off';
  const setChartsVisible = useCallback(
    (on: boolean) => setFilters({ charts: on ? 'on' : 'off' }),
    [setFilters],
  );
  const [agingBucket, setAgingBucket] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('agg_banner_dismissed_v1') === '1';
  });

  const aggregatorChannels = useMemo(
    () => allChannels.filter(
      (c) => (c.channelType === 'ONLINE_AGGREGATOR' || c.type === 'ONLINE_AGGREGATOR')
        && c.isActive !== false,
    ),
    [allChannels],
  );
  const hasAggregators = aggregatorChannels.length > 0;

  const [summary, setSummary] = useState<AggregatorSummary | null>(null);
  const [entries, setEntries] = useState<AggregatorEntry[]>([]);
  const [settlements, setSettlements] = useState<AggregatorSettlement[]>([]);
  const [aging, setAging] = useState<AgingResponse | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateAllocation[]>([]);
  const [loading, setLoading] = useState(true);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerChannelId, setDrawerChannelId] = useState<number | undefined>(undefined);
  const [drawerDate, setDrawerDate] = useState(daysAgo(0));
  const [drawerPeriodFrom, setDrawerPeriodFrom] = useState('');
  const [drawerPeriodTo, setDrawerPeriodTo] = useState('');
  const [drawerAmount, setDrawerAmount] = useState('');
  const [drawerNotes, setDrawerNotes] = useState('');
  const [drawerSuggested, setDrawerSuggested] = useState<SuggestedAllocations | null>(null);
  const [drawerSubmitting, setDrawerSubmitting] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);

  // Bidirectional hover-link state for entry <-> settlement visual binding
  const [hoveredSettlementId, setHoveredSettlementId] = useState<number | null>(null);
  const [hoveredEntryKey, setHoveredEntryKey] = useState<string | null>(null);

  // settlementId -> Set<"entryType-entryId">
  const settlementToEntryKeys = useMemo(() => {
    const map = new Map<number, Set<string>>();
    settlements.forEach((s) => {
      map.set(
        s.id,
        new Set((s.allocations ?? []).map((a) => `${a.entryType}-${a.entryId}`)),
      );
    });
    return map;
  }, [settlements]);

  // "entryType-entryId" -> settlementId
  const entryKeyToSettlement = useMemo(() => {
    const map = new Map<string, number>();
    entries.forEach((e) => {
      if (e.settlementId) map.set(`${e.entryType}-${e.id}`, e.settlementId);
    });
    return map;
  }, [entries]);

  // Relationship-color palette (Scandinavian-warm, muted, distinguishable).
  // Each settlement maps deterministically to a hue; covered entries inherit it.
  // The bar communicates "which settlement does this belong to", not status.
  const RELATIONSHIP_PALETTE = useMemo(
    () => [
      '#5B8C7E', // muted teal
      '#C97B63', // terracotta
      '#8B7AA8', // dusty violet
      '#C9A961', // ochre
      '#6B8FA8', // slate blue
      '#B5757E', // muted rose
    ],
    [],
  );

  const colorForSettlement = useCallback(
    (settlementId: number): string =>
      RELATIONSHIP_PALETTE[settlementId % RELATIONSHIP_PALETTE.length],
    [RELATIONSHIP_PALETTE],
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, entriesRes, settlementsRes, agingRes, dupRes] = await Promise.all([
        aggregatorApi.summary({ channelId, dateFrom, dateTo }),
        aggregatorApi.entries({ channelId, dateFrom, dateTo }),
        aggregatorApi.settlements({ channelId, dateFrom, dateTo }),
        aggregatorApi.aging({ channelId }),
        aggregatorApi.duplicateAllocations(),
      ]);
      setSummary(summaryRes.data);
      setEntries(entriesRes.data);
      setSettlements(settlementsRes.data);
      setAging(agingRes.data);
      setDuplicates(Array.isArray(dupRes.data) ? dupRes.data : []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load aggregator data';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [channelId, dateFrom, dateTo]);

  useEffect(() => {
    financeApi
      .getChannels()
      .then((res) => {
        setAllChannels(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setAllChannels([]));
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Drawer auto-suggest
  useEffect(() => {
    if (!drawerOpen) return;
    if (!drawerChannelId || !drawerAmount) {
      setDrawerSuggested(null);
      return;
    }
    const amt = parseFloat(drawerAmount);
    if (Number.isNaN(amt) || amt <= 0) return;
    aggregatorApi
      .suggestAllocations({
        channelId: drawerChannelId,
        paymentAmount: amt,
        paymentDate: drawerDate,
        periodFrom: drawerPeriodFrom || undefined,
        periodTo: drawerPeriodTo || undefined,
      })
      .then((res) => setDrawerSuggested(res.data))
      .catch(() => setDrawerSuggested(null));
  }, [drawerOpen, drawerChannelId, drawerAmount, drawerDate, drawerPeriodFrom, drawerPeriodTo]);

  const openRecordDrawer = () => {
    setDrawerChannelId(channelId);
    setDrawerAmount('');
    setDrawerDate(daysAgo(0));
    setDrawerPeriodFrom('');
    setDrawerPeriodTo('');
    setDrawerNotes('');
    setDrawerSuggested(null);
    setDrawerError(null);
    setDrawerOpen(true);
  };

  // Deep-link prefill from the assistant document inbox ("Edit in form"):
  // ?record=1&channelId=..&amount=..&paymentDate=..&periodFrom=..&periodTo=..
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('record') !== '1') return;
    const prefillChannel = params.get('channelId');
    setDrawerChannelId(prefillChannel ? Number(prefillChannel) : undefined);
    setDrawerAmount(params.get('amount') ?? '');
    setDrawerDate(params.get('paymentDate') ?? daysAgo(0));
    setDrawerPeriodFrom(params.get('periodFrom') ?? '');
    setDrawerPeriodTo(params.get('periodTo') ?? '');
    setDrawerNotes('');
    setDrawerSuggested(null);
    setDrawerError(null);
    setDrawerOpen(true);
    // strip params so back/refresh doesn't re-open the drawer
    window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitSettlement = async () => {
    setDrawerError(null);
    if (!drawerChannelId || !drawerAmount) {
      setDrawerError('Channel and amount are required');
      return;
    }
    setDrawerSubmitting(true);
    try {
      await aggregatorApi.recordSettlement({
        channelId: drawerChannelId,
        paymentDate: drawerDate,
        periodFrom: drawerPeriodFrom || undefined,
        periodTo: drawerPeriodTo || undefined,
        paymentAmount: parseFloat(drawerAmount),
        notes: drawerNotes || undefined,
        source: 'MANUAL',
      });
      toast.success('Settlement recorded');
      setDrawerOpen(false);
      fetchAll();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message || 'Failed to record settlement';
      setDrawerError(msg);
    } finally {
      setDrawerSubmitting(false);
    }
  };

  const exportAging = async () => {
    try {
      const res = await aggregatorApi.agingExport({ channelId, ageDays: 14 });
      const blob = new Blob([res.data as BlobPart], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aggregator-aging.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export aging');
    }
  };

  const channelChips = useMemo(
    () => [
      { id: undefined as number | undefined, name: hasAggregators ? 'All aggregators' : 'All channels' },
      ...aggregatorChannels.map((c) => ({ id: c.id, name: c.name })),
    ],
    [aggregatorChannels, hasAggregators],
  );

  const dismissBanner = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('agg_banner_dismissed_v1', '1');
    }
    setBannerDismissed(true);
  };

  const inputSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components.input.background,
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components.input.border },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components.input.focus || theme.primary },
    },
    '& .MuiInputBase-input': {
      color: theme.text,
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      fontSize: '0.875rem',
    },
  };

  return (
    <Box sx={{ p: '16px 16px 32px', backgroundColor: theme.background, minHeight: '100vh' }}>
      <FinanceTabBar />
      {/* Header — confident-shell hero band */}
      <PageHeroBand
        eyebrow="Finance · Aggregators"
        title="Aggregators"
        actions={<PrimaryButton onClick={openRecordDrawer}>Record Settlement</PrimaryButton>}
      />

      {/* Onboarding banner — mirrors the Visual2DLayoutManager transfer-mode
          banner pattern (full-bleed, gradient, icon + bold text + action).
          Case A: no aggregator channels yet — sticky warn, non-dismissible CTA.
          Case B: aggregator(s) configured — subtle info row, dismiss persists
                  via localStorage. */}
      {!hasAggregators && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
            color: 'white',
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            mx: { xs: -2, sm: -3 },
            mb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 1.5 },
            justifyContent: 'space-between',
            borderRadius: 0,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
              ⚠ No aggregators set up yet
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.813rem' }, opacity: 0.95, mt: 0.25 }}>
              Mark a channel as &ldquo;Online Aggregator&rdquo; (Uber Eats, Deliveroo…) so settlement KPIs only count delivery revenue. Right now this page shows all channels.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/vendor/finance/settings?modal=aggregator')}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.6)',
              fontWeight: 600,
              flexShrink: 0,
              alignSelf: { xs: 'flex-end', sm: 'center' },
              whiteSpace: 'nowrap',
              minHeight: 44,
              '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }, /* basil-todo */
              '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
            }}
          >
            Configure in Settings →
          </Button>
        </Box>
      )}

      {hasAggregators && !bannerDismissed && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            mb: 2,
            borderRadius: '8px',
            backgroundColor: `${theme.primary}10`,
            border: `1px solid ${theme.primary}30`,
          }}
        >
          <Typography sx={{ fontSize: '0.813rem', color: theme.text, flex: 1 }}>
            ℹ Showing {aggregatorChannels.length} aggregator channel{aggregatorChannels.length === 1 ? '' : 's'}:{' '}
            <strong>{aggregatorChannels.map((c) => c.name).join(', ')}</strong>
          </Typography>
          <Button
            size="small"
            onClick={() => navigate('/vendor/finance/settings?modal=aggregator')}
            sx={{ fontSize: '0.75rem', color: theme.primary, textTransform: 'none', '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' } }}
          >
            Manage →
          </Button>
          <Button
            size="small"
            onClick={dismissBanner}
            sx={{ minWidth: 28, p: 0.5, color: theme.gray[500], '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' } }}
            aria-label="Dismiss banner"
          >
            ✕
          </Button>
        </Box>
      )}

      {/* Channel filter chips — heavy active state, banner below confirms filter. */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        {channelChips.map((c) => {
          const active = channelId === c.id;
          return (
            <Chip
              key={String(c.id)}
              label={c.name}
              onClick={() => setChannelId(c.id)}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                fontWeight: active ? 700 : 500,
                // Soft ochre "selected" state (tint + ochre text + ring), not a
                // heavy solid fill — readable at small sizes, matches EOD chips.
                backgroundColor: active ? `${theme.primary}1A` : (theme.gray?.[100] || '#F0F0F0'),
                color: active ? theme.primary : (theme.gray?.[700] || '#444'),
                border: active
                  ? `1.5px solid ${theme.primary}`
                  : `1px solid ${theme.gray?.[300] || '#D0D0D0'}`,
                '&:hover': {
                  backgroundColor: active ? `${theme.primary}26` : (theme.gray?.[200] || '#E0E0E0'),
                },
              }}
            />
          );
        })}
      </Box>

      {/* Entry status filter chips (applies to Reported Entries list only) */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        {ENTRY_STATUS_CHIPS.map((s) => {
          const active = entryStatus === s.value;
          return (
            <Chip
              key={s.value || 'all'}
              label={s.label}
              onClick={() => setEntryStatus(s.value)}
              size="small"
              variant="outlined"
              sx={{
                cursor: 'pointer',
                fontWeight: active ? 700 : 500,
                // Soft ochre "selected" state (tint + ochre text + ring), not a
                // heavy solid fill — readable at small sizes, matches EOD chips.
                backgroundColor: active ? `${theme.primary}1A` : (theme.gray?.[100] || '#F0F0F0'),
                color: active ? theme.primary : (theme.gray?.[700] || '#444'),
                border: active
                  ? `1.5px solid ${theme.primary}`
                  : `1px solid ${theme.gray?.[300] || '#D0D0D0'}`,
                '&:hover': {
                  backgroundColor: active ? `${theme.primary}26` : (theme.gray?.[200] || '#E0E0E0'),
                },
              }}
            />
          );
        })}
      </Box>

      {/* Active filter banner — visible confirmation of what's narrowing the view. */}
      {(channelId != null || entryStatus) && (() => {
        const ch = channelChips.find((c) => c.id === channelId);
        const st = ENTRY_STATUS_CHIPS.find((s) => s.value === entryStatus);
        const parts: string[] = [];
        if (ch && ch.id != null) parts.push(`channel: ${ch.name}`);
        if (st && st.value) parts.push(`status: ${st.label}`);
        if (parts.length === 0) return null;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              mb: 2,
              borderRadius: '8px',
              backgroundColor: `${theme.primary}14`,
              border: `1px solid ${theme.primary}44`,
            }}
          >
            <Typography sx={{ fontSize: '0.813rem', color: theme.primary, fontWeight: 600, flex: 1 }}>
              ● Filtering by {parts.join(' · ')}
              {channelId != null ? ' · graphs scoped to this channel' : ''}
            </Typography>
            <Chip
              label="Clear ✕"
              size="small"
              onClick={() => {
                setChannelId(undefined);
                setEntryStatus('');
              }}
              sx={{
                cursor: 'pointer',
                backgroundColor: 'transparent',
                color: theme.primary,
                fontWeight: 700,
                border: `1px solid ${theme.primary}66`,
                '&:hover': { backgroundColor: `${theme.primary}22` },
              }}
            />
          </Box>
        );
      })()}

      {/* Date range — pair wraps to its own row on narrow screens */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <FinanceDateRange
          from={dateFrom}
          to={dateTo}
          onFromChange={setDateFrom}
          onToChange={setDateTo}
          sx={{ flex: '1 1 260px' }}
        />
        <Tooltip
          arrow
          placement="top"
          title={chartsVisible ? 'Collapse the insights strip' : 'Aging breakdown · Settlement timeline · Variance'}
        >
          <Chip
            icon={
              chartsVisible
                ? <EyeSlashIcon style={{ width: 14, height: 14 }} />
                : <ChartBarIcon style={{ width: 14, height: 14 }} />
            }
            label={chartsVisible ? 'Hide insights' : 'Show insights'}
            onClick={() => setChartsVisible(!chartsVisible)}
            data-testid="aggregators-toggle-charts"
            sx={{
              fontSize: '0.75rem',
              height: 32,
              fontWeight: 700,
              cursor: 'pointer',
              px: 0.5,
              flexShrink: 0,
              backgroundColor: chartsVisible ? (theme.gray?.[100] || '#F0F0F0') : `${theme.primary}1f`,
              color: chartsVisible ? (theme.gray?.[600] || '#555') : theme.primary,
              border: chartsVisible ? `1px solid ${theme.gray?.[300] || '#D0D0D0'}` : `1.5px solid ${theme.primary}66`,
              '& .MuiChip-icon': { color: chartsVisible ? (theme.gray?.[600] || '#555') : theme.primary, marginLeft: '6px' },
              '&:hover': { backgroundColor: chartsVisible ? (theme.gray?.[200] || '#E0E0E0') : `${theme.primary}33` },
            }}
          />
        </Tooltip>
      </Box>

      {/* Filter-aware totals over the currently listed settlements */}
      {!loading && settlements.length > 0 && (
        <FilteredTotals
          data-testid="aggregator-filtered-totals"
          items={[
            {
              label: 'Settlements',
              value: formatCurrency(settlements.reduce((sum, s) => sum + Number(s.paymentAmount ?? 0), 0)),
            },
            {
              label: 'Commission',
              value: formatCurrency(settlements.reduce((sum, s) => sum + Number(s.commissionActual ?? 0), 0)),
            },
            { label: 'Count', value: String(settlements.length) },
          ]}
        />
      )}

      {!loading && duplicates.length > 0 && (
        <CardContainer
          sx={{
            p: 1.5,
            mb: 2,
            backgroundColor: `${theme.danger}12`,
            border: `1px solid ${theme.danger}55`,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: theme.danger, mb: 0.5 }}>
            ⚠ {duplicates.length} entry{duplicates.length === 1 ? '' : 'ies'} allocated to multiple settlements
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: theme.gray[700], mb: 1 }}>
            One entry can only belong to one settlement. Review and correct these manually.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {duplicates.slice(0, 10).map((d) => (
              <Typography
                key={`${d.entryType}-${d.entryId}`}
                sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}
              >
                {d.entryType} #{d.entryId}
                {d.entryDate ? ` · ${d.entryDate}` : ''}
                {d.channelName ? ` · ${d.channelName}` : ''}
                {' → settlements '} {d.settlementIds.join(', ')}
                {d.entryGross != null ? ` · gross ${formatCurrency(d.entryGross)}` : ''}
              </Typography>
            ))}
            {duplicates.length > 10 && (
              <Typography sx={{ fontSize: '0.7rem', color: theme.gray[600] }}>
                …and {duplicates.length - 10} more
              </Typography>
            )}
          </Box>
        </CardContainer>
      )}

      {loading && <LoadingSkeleton variant="card" count={3} />}

      {/* KPI strip */}
      {!loading && summary && (
        <CardContainer sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2 }}>
            <Kpi label="Reported Gross" value={formatCurrency(summary.reportedGross)} />
            <Kpi label="Settled" value={formatCurrency(summary.settled)} />
            <Kpi label="Pending" value={formatCurrency(summary.pending)} />
            <Kpi
              label="Effective Commission"
              value={`${Number(summary.effectiveCommissionPercent).toFixed(2)}%`}
            />
            <Kpi label="Unexplained Variance" value={formatCurrency(summary.unexplainedVariance)} />
          </Box>
        </CardContainer>
      )}

      {/* Insight charts */}
      {!loading && chartsVisible && (
        <AggregatorCharts
          aging={aging}
          settlements={settlements}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedAgingBucket={agingBucket}
          onAgingClick={(label) => {
            setAgingBucket(label);
            // narrow entries list to non-settled when age filter active
            if (label && entryStatus === 'SETTLED') setEntryStatus('');
          }}
          onExportAging={exportAging}
          onSettlementClick={(id) => navigate(`/vendor/finance/aggregators/settlement/${id}`)}
        />
      )}

      {/* Two-column layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* Entries timeline */}
        <CardContainer sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1.5 }}>Reported Entries</Typography>
          {(() => {
            const ageBucketMatch = (age: number, bucket: string | null): boolean => {
              if (!bucket) return true;
              if (bucket === '0-7d') return age < 7;
              if (bucket === '7-14d') return age >= 7 && age < 14;
              if (bucket === '14-30d') return age >= 14 && age < 30;
              if (bucket === '30+d') return age >= 30;
              return true;
            };
            const filteredEntries = entries.filter((e) => {
              if (entryStatus && (e.settlementStatus || 'PENDING') !== entryStatus) return false;
              if (agingBucket) {
                // aging is computed from pending entries only
                if ((e.settlementStatus || 'PENDING') === 'SETTLED') return false;
                if (!ageBucketMatch(e.ageDays || 0, agingBucket)) return false;
              }
              return true;
            });
            return filteredEntries.length === 0 ? (
              <EmptyState title={entryStatus || agingBucket ? `No matching entries` : 'No entries in this range'} />
            ) : (
              filteredEntries.map((e) => {
              const entryKey = `${e.entryType}-${e.id}`;
              const isLinkedToHoveredSettlement =
                hoveredSettlementId !== null &&
                (settlementToEntryKeys.get(hoveredSettlementId)?.has(entryKey) ?? false);
              const hasSettlement = typeof e.settlementId === 'number' && e.settlementId > 0;
              const borderLeft = hasSettlement
                ? `3px solid ${colorForSettlement(e.settlementId as number)}`
                : `3px dashed ${theme.gray[300]}`;
              return (
                <Box
                  key={entryKey}
                  onMouseEnter={() => setHoveredEntryKey(entryKey)}
                  onMouseLeave={() => setHoveredEntryKey(null)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    pl: 1.25,
                    pr: 1,
                    ml: -1,
                    mr: -1,
                    borderBottom: `1px solid ${theme.gray[200]}`,
                    borderLeft,
                    backgroundColor: isLinkedToHoveredSettlement
                      ? `${theme.primary}22`
                      : 'transparent',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {e.channelName ?? '—'} · {e.date}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: theme.gray[500] }}>
                      {e.ageDays}d · {e.settlementStatus}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      {formatCurrency(e.grossAmount)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          );
          })()}
        </CardContainer>

        {/* Settlements */}
        <CardContainer sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1.5 }}>Settlements</Typography>
          {settlements.length === 0 ? (
            <EmptyState title="No settlements in this range" />
          ) : (
            settlements.map((s) => {
              const isLinkedToHoveredEntry =
                hoveredEntryKey !== null &&
                entryKeyToSettlement.get(hoveredEntryKey) === s.id;
              const sBarColor = colorForSettlement(s.id);
              return (
              <Box
                key={s.id}
                onClick={() => navigate(`/vendor/finance/aggregators/settlement/${s.id}`)}
                onMouseEnter={() => setHoveredSettlementId(s.id)}
                onMouseLeave={() => setHoveredSettlementId(null)}
                sx={{
                  borderBottom: `1px solid ${theme.gray[200]}`,
                  borderLeft: `3px solid ${sBarColor}`,
                  py: 1.5,
                  pl: 1.25,
                  pr: 1,
                  ml: -1,
                  mr: -1,
                  cursor: 'pointer',
                  backgroundColor: isLinkedToHoveredEntry
                    ? `${theme.primary}22`
                    : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': { backgroundColor: `${theme.primary}22` },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    {s.channelName} · {s.paymentDate}
                    {s.periodFrom && (
                      <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, color: theme.gray[500], ml: 1 }}>
                        (covers {s.periodFrom}{s.periodTo && s.periodTo !== s.periodFrom ? ` → ${s.periodTo}` : ''})
                      </Typography>
                    )}
                  </Typography>
                  <StatusChip status={s.status} variant="info" />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mt: 1 }}>
                  <Mini label="Reported" value={formatCurrency(s.grossReported)} />
                  <Mini label="Paid" value={formatCurrency(s.paymentAmount)} />
                  <Mini label="Commission" value={formatCurrency(s.commissionActual)} />
                  <Mini label="Variance" value={formatCurrency(s.unexplainedVariance)} />
                </Box>
              </Box>
              );
            })
          )}
        </CardContainer>
      </Box>

      {/* Record drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 2 } }}
      >
        <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 2 }}>
          Record Aggregator Settlement
        </Typography>
        <Stack spacing={2}>
          <TextField
            select
            label="Channel"
            value={drawerChannelId ?? ''}
            onChange={(e) => setDrawerChannelId(Number(e.target.value))}
            size="small"
            sx={inputSx}
          >
            {aggregatorChannels.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label="Payment Date"
            value={drawerDate}
            onChange={(e) => setDrawerDate(e.target.value)}
            size="small"
            sx={inputSx}
            InputLabelProps={{ shrink: true }}
            helperText="Day money landed"
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              type="date"
              label="Period From"
              value={drawerPeriodFrom}
              onChange={(e) => setDrawerPeriodFrom(e.target.value)}
              size="small"
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="Period To"
              value={drawerPeriodTo}
              onChange={(e) => setDrawerPeriodTo(e.target.value)}
              size="small"
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
              helperText="Days of revenue this payout covers"
            />
          </Box>
          <TextField
            label="Payment Amount"
            type="number"
            value={drawerAmount}
            onChange={(e) => setDrawerAmount(e.target.value)}
            size="small"
            sx={inputSx}
          />
          <TextField
            label="Notes"
            multiline
            rows={2}
            value={drawerNotes}
            onChange={(e) => setDrawerNotes(e.target.value)}
            size="small"
            sx={inputSx}
          />
          {drawerSuggested && (
            <Box sx={{ p: 1, backgroundColor: theme.gray[100], borderRadius: 2 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                Suggested allocations
              </Typography>
              {drawerSuggested.suggested.map((a) => (
                <Box
                  key={`${a.entryType}-${a.entryId}`}
                  sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.813rem' }}
                >
                  <span>{a.sourceLabel ?? `${a.entryType} #${a.entryId}`}</span>
                  <span>{formatCurrency(a.allocatedAmount)}</span>
                </Box>
              ))}
              <Typography sx={{ mt: 1, fontSize: '0.75rem', color: theme.gray[500] }}>
                Cumulative gross: {formatCurrency(drawerSuggested.cumulativeGross)} · residual{' '}
                {formatCurrency(drawerSuggested.residual)}
              </Typography>
            </Box>
          )}
          {drawerError && (
            <Box
              role="alert"
              sx={{
                p: 1.25,
                borderRadius: 2,
                backgroundColor: `${theme.danger}18`,
                border: `1px solid ${theme.danger}55`,
                color: theme.danger,
                fontSize: '0.85rem',
                fontWeight: 500,
              }}
            >
              {drawerError}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <SecondaryButton onClick={() => setDrawerOpen(false)} disabled={drawerSubmitting}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={submitSettlement} disabled={drawerSubmitting}>
              {drawerSubmitting ? 'Saving…' : 'Save Settlement'}
            </PrimaryButton>
          </Box>
        </Stack>
      </Drawer>
    </Box>
  );
};

const Kpi: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: '0.688rem', color: '#888', mb: 0.25 }}>{label}</Typography> {/* basil-todo */}
    <Typography sx={{ fontSize: '1.125rem', fontWeight: 700 }}>{value}</Typography>
  </Box>
);

const Mini: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: '0.625rem', color: '#888' }}>{label}</Typography> {/* basil-todo */}
    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export default AggregatorsPage;
